import Chartist, { IChartistData, ILineChartOptions } from 'chartist';
import React from 'react';
import ChartistGraph from 'react-chartist';
import { IHall, IObject, WorkTimeForADay } from '../App';

export type ReservationLoadRequestBody = {
  start: number,
  end: number,
};

export type ReservationLoadResponse = {
  startDate: number,
  endDate: number,
  reservedPlaces: number,
};

type ISingleHallOccupancyData = {
  label: string,
  persons: number,
};

type IHallOccupancyData = {
  maxPersons: number,
  data: ISingleHallOccupancyData[],
};

type Properties = {  
  object: IObject,
  hall: IHall,
  date: Date,
};

type State = {
  data: IHallOccupancyData | undefined,
};

class HallOccupancyGraph extends React.Component<Properties, State>
{    
    constructor(props: Properties)
    {
      super(props);   

      this.fetchHallOccupancyData = this.fetchHallOccupancyData.bind(this);
      this.makeTimeFromWorktime = this.makeTimeFromWorktime.bind(this);
      this.fetchObjectWorkTime = this.fetchObjectWorkTime.bind(this);
      
      this.state = {
        data: undefined,
      }
    }

    /**
     * 
     * @param isStart true for worktime-start, false for worktime-end
     */
    makeTimeFromWorktime(isStart: boolean, workTime: WorkTimeForADay[])
    {
      let date: Date = new Date(this.props.date);
      date.setHours( 0, 0, 0, 0 );

      let dayWorkTime: WorkTimeForADay | undefined = workTime.find( value => {
        return value.dayOfWeek === date.getDay();
      });

      if(dayWorkTime) 
      {
        if(isStart)
        {
          date.setHours( dayWorkTime.startHour, dayWorkTime.startMinute, 0, 0 );
        }
        else
        {
          date.setHours( dayWorkTime.endHour, dayWorkTime.endMinute, 0, 0 );
        }
      }

      return date;
    }

    async fetchObjectWorkTime()
    {
      return fetch(`${process.env.REACT_APP_API_PREFIX}/reservations/api/getworktime?objectId=${encodeURIComponent(this.props.object.id)}`, { credentials: 'include' })
      .then( response => { 
        return response.json().then( value => {
          return value;
        });               
      }).catch( (reason) => {
        console.error(reason);
      });
    }

    async fetchHallOccupancyData()
    {      
      let worktime = await this.fetchObjectWorkTime();

      let start: Date = this.makeTimeFromWorktime(true, worktime);
      let end: Date = this.makeTimeFromWorktime(false, worktime);
      let interval: number = this.props.object.interval * 60 * 1000;

      let requestBody: ReservationLoadRequestBody[] = []; 


      for(let time: number = start.getTime(); time <= end.getTime(); time += interval)
      {

        requestBody.push( {
          start: (time / 1000) + 1,
          end: ((time + interval) / 1000),
        });
      }

      fetch(`${process.env.REACT_APP_API_PREFIX}/reservations/api/gethalloccupancydata`,
        {
          method: 'post',
          body: JSON.stringify({
            'objectId': this.props.object.id,
            'hallId': this.props.hall.id,
            'intervals': requestBody 
          }),
          headers: {
            'Content-Type': 'application/json',
          },    
          credentials: 'include',  
        }
      ).then( response => {
        response.json().then( (response: ReservationLoadResponse[]) => {          
          
          let data: IHallOccupancyData = {
            data: [],
            maxPersons: this.props.hall.capacity,
          };

          response.forEach( singleResponse => {
            data.data.push( {
              persons: singleResponse.reservedPlaces,
              label: new Date((singleResponse.startDate) * 1000).toLocaleString(navigator.language, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }),
            });
          });

          this.setState( () => {
            return { data: data };
          });
        });
      }).catch( reason => {

        console.log(reason);

        this.setState( () => {
          return { data: undefined };
        })

      });
    }

    componentDidMount()
    {
      this.fetchHallOccupancyData();
    }

    componentDidUpdate(prevProps: Properties, prevState: State)
    {
      if( (this.props.hall.id !== prevProps.hall.id) || (this.props.date.getTime() !== prevProps.date.getTime()) )
      {
        this.fetchHallOccupancyData();
      }
    }

    render()
    {    
      if(!this.state.data) return null;

      let seriesData: number[] = [];
      let maxData: number[] = [];
      let seriesLabels: string[] = [];

      this.state.data.data.forEach( (singlePointData) => {
        seriesData.push(singlePointData.persons);
        maxData.push(this.props.hall.capacity);
        seriesLabels.push(singlePointData.label);
      });

      let options: ILineChartOptions = {
        axisX: {
            onlyInteger: true,
            showGrid: true,     
            showLabel: true,            
            labelInterpolationFnc: (value: any, index: number, labels: any[]) => {
              let skipFactor = Math.max(Math.round(1 / (8 / labels.length)), 1);                            
              return index % skipFactor === 0 ? value : null;              
            },
            labelOffset: {
              x: -10,
              y: 0,
            }
        },     
        axisY: {          
          onlyInteger: true,
          showGrid: true,
          showLabel: true,   
        },
        chartPadding: {
          left: 0,
          right: 15,
          top: 25,
          bottom: 0
        },
        low: 0,
        high: this.props.hall.capacity,     
        showPoint: true,          
        fullWidth: true,
        lineSmooth: Chartist.Interpolation.step(),
      }

      let data: IChartistData = {
        series: 
        [          
          {
            name: 'series-1',
            data: seriesData
          },
          {
            name: 'max-series',
            data: maxData
          }
        ],
        labels: seriesLabels
      };

      let type: string = 'Line';

      return (
          <div>
            <ChartistGraph data={data} options={options} type={type} />
          </div>
      );
    }
};

export default HallOccupancyGraph;